// Loads under node --test: errors.ts is import-free.
import { normalizeError } from '../../api/core/errors.ts';
import { msg, type MaybeMsg, type Msg } from '../../i18n/msg.ts';

export interface SsoFailure {
  /** izteach-admin-service's own code from the body (400/403/409/500/502); null when no body came back */
  code: number | null;
  /** the notice line: a Msg for app wording, a plain string for the backend's own text */
  message: MaybeMsg;
  /** 409: an IZ account with this email or phone exists but is not linked; only an admin can fix it */
  needsLink: boolean;
}

export function ssoNeedsLinkMessage(): Msg {
  return msg('errors:sso.needsLink');
}

// The admin service answers every error with HTTP 500 and puts the real code in `body.code`, the text in `body.errorMessage`.
function bodyField(data: unknown, key: string): unknown {
  return data && typeof data === 'object' ? (data as Record<string, unknown>)[key] : undefined;
}

export function ssoFailure(error: unknown): SsoFailure {
  const normalized = normalizeError(error);
  if (normalized.kind === 'network') return { code: null, message: msg('errors:sso.network'), needsLink: false };
  if (normalized.kind === 'timeout') return { code: null, message: msg('errors:sso.timeout'), needsLink: false };

  const rawCode = bodyField(normalized.data, 'code');
  const code = typeof rawCode === 'number' ? rawCode : typeof rawCode === 'string' && /^\d+$/.test(rawCode) ? Number(rawCode) : null;
  const text = bodyField(normalized.data, 'errorMessage');
  const reason = typeof text === 'string' && text ? text : '';

  if (code === 409) return { code, message: ssoNeedsLinkMessage(), needsLink: true };
  if (reason) return { code, message: reason, needsLink: false };
  return { code: code || normalized.status, message: msg('errors:sso.server'), needsLink: false };
}
